import TemplateBlueprints from '../components/PageAssembler/TemplateBlueprints'

const normalizePageId = (value) => {
  if (value === 'front' || value === 'back') {
    return value
  }

  if (typeof value === 'number') {
    return value
  }

  const parsed = Number(value)
  return Number.isNaN(parsed) ? value : parsed
}

export function initialMemoryBookState() {
  return {
    createdDate: new Date(),
    title: 'My Memory Book',
    description: '',
    contentPages: [
      {
        page: 'back',
        templateId: 18,
        elements: TemplateBlueprints(18, {}),
      },
      {
        page: 'front',
        templateId: 1,
        elements: TemplateBlueprints(1, [{}]),
      },
      {
        page: 1,
        templateId: 6,
        elements: TemplateBlueprints(6, [{}]),
      },
      {
        page: 2,
        templateId: 15,
        elements: TemplateBlueprints(15, [{}]),
      },
      {
        page: 3,
        templateId: 5,
        elements: TemplateBlueprints(5, [{}]),
      },
      {
        page: 4,
        templateId: 16,
        elements: TemplateBlueprints(16, [{}]),
      },
      {
        page: 5,
        templateId: 12,
        elements: TemplateBlueprints(12, [{}]),
      },
      {
        page: 6,
        templateId: 2,
        elements: TemplateBlueprints(2, {}),
      },
    ],
    currentPages: ['back', 'front'],
  }
}

export function newPageState(pageNum, templateId = 0, props = {}) {
  const elements = TemplateBlueprints(templateId, props)
  return {
    page: pageNum,
    templateId,
    elements,
  }
}

function adjustCurrentViewingPages(pages, focusPage, isMobile = false) {
  const totalPages = pages.length

  if (isMobile) {
    if (focusPage === 0) {
      return ['front'] // If the first page is deleted and we have no more pages, go to the front cover
    }
    return [focusPage]
  }
  // If there are no pages left (only front & back remain), return default view
  if (totalPages === 2) {
    return ['back', 'front']
  }

  // If the book is reduced to a single remaining content page, show just that one
  if (totalPages === 3) {
    return [1]
  }

  // Edge case: If the last page is alone (odd total count), show it by itself
  if (totalPages % 2 !== 0 && focusPage >= totalPages - 2) {
    return [focusPage]
  }

  // Special case: If deleting the first page, always return [1,2] if possible
  if (focusPage === 0 || focusPage === 1) {
    return totalPages > 3 ? [1, 2] : [1]
  }

  // If the focus page is odd, show it with the next page
  if (focusPage % 2 !== 0) {
    return [focusPage, focusPage + 1]
  }

  // If the focus page is even, show it with the previous page
  return [focusPage - 1, focusPage]
}

export function memoryBooksReducer(state, action) {
  switch (action.type) {
    case 'ADD_PAGE': {
      const { addAfterThisPageNum, isMobile, response } = action.payload

      const newPages = response.map((page) => ({ ...page })) // Copy state to avoid mutation

      const firstInsertedPage = addAfterThisPageNum + 1 // The first page that was inserted

      return {
        ...state,
        contentPages: response,
        currentPages: adjustCurrentViewingPages(newPages, firstInsertedPage, isMobile),
      }
    }

    case 'DELETE_PAGE': {
      const { pageNumber, isMobile, response } = action.payload

      return {
        ...state,
        contentPages: response,
        currentPages: adjustCurrentViewingPages(response, pageNumber - 1, isMobile),
      }
    }

    case 'TURN_PAGE': {
      const { direction, isMobile } = action.payload
      const { currentPages, contentPages } = state
      const lastPageNumber = contentPages[contentPages.length - 1]?.page || 0
      let newCurrentPages = [...currentPages]

      const turnForward = () => {
        const [leftPage, rightPage] = currentPages
        if (isMobile) {
          if (leftPage === 'back') {
            return ['front']
          }
          if (leftPage === 'front') {
            return [1]
          }
          // Check if next page exists
          if (leftPage >= lastPageNumber) {
            return [leftPage]
          }
          return [leftPage + 1]
        }

        if (currentPages?.includes('front')) {
          return contentPages.length > 3 ? [1, 2] : [1]
        }
        if (rightPage >= lastPageNumber - 1) {
          return [lastPageNumber]
        }
        const nextLeftPage = leftPage + 2
        const nextRightPage = rightPage + 2
        return nextRightPage > lastPageNumber ? [nextLeftPage] : [nextLeftPage, nextRightPage]
      }

      const turnBackward = () => {
        const [leftPage] = currentPages
        if (isMobile) {
          if (leftPage === 1) {
            return ['front']
          }
          if (leftPage === 'front') {
            return ['back']
          }
          // Check if previous page exists
          if (leftPage <= 1) {
            return [leftPage]
          }
          return [leftPage - 1]
        }

        if (currentPages?.includes(1)) {
          return ['back', 'front']
        }
        if (leftPage === lastPageNumber) {
          return [lastPageNumber - 2, lastPageNumber - 1]
        }
        const newFirstPage = leftPage - 2 > 0 ? leftPage - 2 : 1
        const newSecondPage = leftPage - 1 > 0 ? leftPage - 1 : 2
        return [newFirstPage, newSecondPage]
      }

      newCurrentPages = direction === 'forward' ? turnForward() : turnBackward()

      return {
        ...state,
        currentPages: newCurrentPages,
      }
    }

    case 'UPDATE_PAGE_ORDER': {
      const { response } = action.payload

      return {
        ...state,
        contentPages: response,
      }
    }

    case 'UPDATE_VIEWING_PAGES': {
      const { pageNumber, isMobile } = action.payload
      const { contentPages, currentPages } = state
      let currentViewingPages = [...currentPages]

      if (isMobile) {
        if (pageNumber === -1 || pageNumber === 'front') {
          currentViewingPages = ['front']
        } else if (pageNumber === 0 || pageNumber === 'back') {
          currentViewingPages = ['back']
        } else {
          currentViewingPages = [pageNumber]
        }
      }

      if (!isMobile) {
        if (pageNumber <= 0 || pageNumber === 'front' || pageNumber === 'back') {
          // Front and back cover
          currentViewingPages = ['back', 'front']
        } else if (pageNumber % 2 === 0) {
          // If the page number is even, show with previous page if it exists
          const prevPageExists = contentPages.some((page) => page.page === pageNumber - 1)
          currentViewingPages = prevPageExists ? [pageNumber - 1, pageNumber] : [pageNumber]
        } else {
          // If the page number is odd, show with next page if it exists
          const nextPageExists = contentPages.some((page) => page.page === pageNumber + 1)
          currentViewingPages = nextPageExists ? [pageNumber, pageNumber + 1] : [pageNumber]
        }
      }

      return {
        ...state,
        currentPages: currentViewingPages,
      }
    }

    case 'UPDATE_PAGE_LAYOUT': {
      const { pageNumber, response } = action.payload
      const { contentPages } = state

      const updatedPages = contentPages.map((page) => {
        if (page?.page === pageNumber) {
          return response
        }
        return page
      })
      return {
        ...state,
        contentPages: updatedPages,
      }
    }

    case 'UPDATE_PAGE_CONTENT': {
      const { pageNumber, positionIndex, updatedId, caption, title, cropArea } = action.payload
      const { contentPages } = state
      const targetPageId = normalizePageId(pageNumber)
      const updatedPages = contentPages.map((page) => {
        if (normalizePageId(page.page) === targetPageId) {
          const updatedElements = page.elements.map((element, index) => {
            if ((element.type === 'image' || element.type === 'backPage') && index === positionIndex) {
              return {
                ...element,
                props: {
                  ...(element.props || {}),
                  memoryId: updatedId !== undefined ? updatedId : element.props.memoryId,
                  caption: caption !== undefined ? caption : element.props.caption,
                  title: title !== undefined ? title : element.props.title,
                  cropArea: cropArea !== undefined ? JSON.stringify(cropArea) : element.props.cropArea,
                },
              }
            }

            if (element.type === 'text' && index === positionIndex) {
              return {
                ...element,
                props: {
                  ...(element.props || {}),
                  memoryId: updatedId !== undefined ? updatedId : element.props.memoryId,
                },
              }
            }
            return element
          })

          return {
            ...page,
            elements: updatedElements,
          }
        }

        return page
      })

      return {
        ...state,
        contentPages: updatedPages,
      }
    }

    case 'UPDATE_PAGE_STICKERS': {
      const { pageNumber, stickerState } = action.payload
      const { contentPages } = state
      const targetPageId = normalizePageId(pageNumber)

      const updatedPages = contentPages.map((page) => {
        if (normalizePageId(page.page) !== targetPageId) {
          return page
        }

        const nextStickers = Array.isArray(stickerState)
          ? stickerState.filter((sticker) => sticker?.id).map((sticker) => ({ ...sticker }))
          : []

        const existingStickers = Array.isArray(page.stickers) ? page.stickers : []

        const existingSerialized = JSON.stringify(existingStickers)
        const nextSerialized = JSON.stringify(nextStickers)

        if (existingSerialized === nextSerialized) {
          return page
        }

        return {
          ...page,
          stickers: nextStickers,
        }
      })

      return {
        ...state,
        contentPages: updatedPages,
      }
    }

    case 'UPDATE_PAGE_BACKGROUND': {
      const { pageNumber, backgroundId } = action.payload
      const { contentPages } = state

      const updatedPages = contentPages.map((page) => {
        if (page.page === pageNumber) {
          return {
            ...page,
            backgroundId,
          }
        }
        return page
      })

      return {
        ...state,
        contentPages: updatedPages,
      }
    }

    case 'SET_MOBILE_MODE': {
      const { isMobile } = action.payload
      const { currentPages, contentPages } = state

      let newCurrentPages = [...currentPages]

      if (isMobile) {
        // Adjust current pages for mobile view - show only the first page
        if (currentPages.length > 1) {
          newCurrentPages = [currentPages[0]]
        }
      } else {
        // Smart desktop view calculation
        const currentPage = currentPages[0]

        if (currentPage === 'front') {
          newCurrentPages = ['back', 'front']
        } else if (currentPage === 'back') {
          newCurrentPages = ['back', 'front']
        } else if (typeof currentPage === 'number') {
          // For numbered pages, show pairs when possible
          const nextPageExists = contentPages.some((page) => page.page === currentPage + 1)
          const prevPageExists = contentPages.some((page) => page.page === currentPage - 1)

          if (currentPage % 2 === 1) {
            // Odd page - show with next page if it exists
            newCurrentPages = nextPageExists ? [currentPage, currentPage + 1] : [currentPage]
          } else {
            // Even page - show with previous page if it exists
            newCurrentPages = prevPageExists ? [currentPage - 1, currentPage] : [currentPage]
          }
        }
      }

      return {
        ...state,
        currentPages: newCurrentPages,
      }
    }

    case 'SET_BOOK': {
      const { currentPages: newCurrentPages, ...restPayload } = action.payload

      // Only use payload's currentPages if we don't have any state yet (initial load)
      const shouldPreserveCurrentPages = state.currentPages && state.currentPages.length > 0
      return {
        ...restPayload,
        currentPages: shouldPreserveCurrentPages ? state.currentPages : newCurrentPages,
      }
    }

    default:
      return state
  }
}
